/** SUNTRONIX ONE — Contracts editor for a master client.
 *
 *  Small, focused page that lets ops add / edit / deactivate MSA and SoW
 *  contracts.  Runtime gate in `POST /api/requests/` refuses new work
 *  when a customer has contracts on file but none of them are currently
 *  active — so this editor is the escape hatch when a contract lapses.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, Save, ArrowLeft, CheckCircle2, AlertCircle, Clock } from "lucide-react";

const CONTRACT_TYPES = ["MSA", "SoW", "Addendum", "NDA", "Other"];

const emptyRow = () => ({
  id: "", contract_number: "", contract_type: "MSA",
  start_date: "", end_date: "", currency: "USD", value: "",
  coverage_notes: "", active: true, signed_by: "", signed_at: "",
});

const STATE_BADGE = {
  active:     { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  expired:    { cls: "bg-red-50 text-red-700 border-red-200",             icon: AlertCircle },
  pending:    { cls: "bg-amber-50 text-amber-700 border-amber-200",       icon: Clock },
  terminated: { cls: "bg-slate-100 text-slate-600 border-slate-300",      icon: AlertCircle },
};

export default function ContractsEditor() {
  const { masterClientId } = useParams();
  const navigate = useNavigate();
  const [mc, setMc] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [anyActive, setAnyActive] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mcRes, cRes] = await Promise.all([
        api.get(`/master-clients/${masterClientId}`),
        api.get(`/master-clients/${masterClientId}/contracts`),
      ]);
      setMc(mcRes.data);
      setRows(cRes.data.items || []);
      setAnyActive(cRes.data.any_active);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load contracts");
    } finally { setLoading(false); }
  }, [masterClientId]);

  useEffect(() => { load(); }, [load]);

  const update = (idx, patch) => setRows((r) =>
    r.map((row, i) => i === idx ? { ...row, ...patch } : row));

  const add = () => setRows((r) => [...r, emptyRow()]);
  const remove = (idx) => setRows((r) => r.filter((_, i) => i !== idx));

  const save = async () => {
    // Server expects `value` as number OR null; strip empty rows
    const payload = rows
      .filter((r) => r.contract_number.trim() && r.start_date && r.end_date)
      .map((r) => ({
        id: r.id || undefined,
        contract_number: r.contract_number.trim(),
        contract_type: r.contract_type || "MSA",
        start_date: r.start_date,
        end_date: r.end_date,
        currency: r.currency || null,
        value: r.value === "" || r.value === null ? null : Number(r.value),
        coverage_notes: r.coverage_notes || null,
        active: r.active !== false,
        signed_by: r.signed_by || null,
        signed_at: r.signed_at || null,
      }));
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/master-clients/${masterClientId}/contracts`, payload);
      toast.success(`${data.count} contract(s) saved.`);
      load();
    } catch (e) {
      const err = e.response?.data?.detail;
      if (err?.issues) {
        toast.error(err.issues.join(" · "));
      } else {
        toast.error(err || "Could not save contracts");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="contracts-editor">
      <PageHeader
        eyebrow={<Link to={`/customers/${masterClientId}`}
          className="flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft className="h-3 w-3" /> Back to customer
        </Link>}
        title={mc ? `Contracts · ${mc.name || mc.legal_name || masterClientId}` : "Contracts"}
        context="MSAs and SoWs that govern this customer's engagement. New service requests are refused when contracts exist on file but none are currently active."
        actions={
          <Button onClick={save} disabled={saving} data-testid="contracts-save">
            {saving ? <Clock className="h-3.5 w-3.5 animate-spin mr-1.5" /> :
              <Save className="h-3.5 w-3.5 mr-1.5" />} Save contracts
          </Button>
        }
        testId="contracts-editor-header"
      />

      {rows.length > 0 && !anyActive && (
        <div className="st-panel bg-red-50 border-red-200 text-red-800 text-[13px] p-3 flex items-start gap-2"
          data-testid="contracts-no-active-warning">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">No active contract on file.</div>
            New service requests for this customer will be refused until an
            active, non-expired contract is present.
          </div>
        </div>
      )}

      <Section title={`${rows.length} contract${rows.length === 1 ? "" : "s"}`}
        testId="contracts-section">
        <div className="st-panel">
          {loading ? (
            <div className="text-slate-400 text-center py-10">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-slate-500 text-center py-10 text-[13px]">
              No contracts yet.  Add one below.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((r, idx) => {
                const badge = STATE_BADGE[r.state] || STATE_BADGE.active;
                const Icon = badge.icon;
                return (
                  <div key={idx} className="p-4 space-y-3"
                    data-testid={`contract-row-${idx}`}>
                    <div className="flex items-center gap-3">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 w-16">
                        Row {idx + 1}
                      </div>
                      {r.state && (
                        <span className={"text-[11px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 " + badge.cls}>
                          <Icon className="h-3 w-3" /> {r.state}
                        </span>
                      )}
                      <div className="ml-auto">
                        <Button variant="ghost" size="sm" onClick={() => remove(idx)}
                          data-testid={`contract-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
                      <Field label="Contract number">
                        <Input value={r.contract_number}
                          onChange={(e) => update(idx, { contract_number: e.target.value })}
                          data-testid={`contract-${idx}-number`} />
                      </Field>
                      <Field label="Type">
                        <select className="input" value={r.contract_type}
                          onChange={(e) => update(idx, { contract_type: e.target.value })}
                          data-testid={`contract-${idx}-type`}>
                          {CONTRACT_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Currency">
                        <Input value={r.currency || ""}
                          onChange={(e) => update(idx, { currency: e.target.value.toUpperCase() })}
                          placeholder="USD / INR / EUR"
                          data-testid={`contract-${idx}-currency`} />
                      </Field>
                      <Field label="Start date">
                        <Input type="date" value={r.start_date || ""}
                          onChange={(e) => update(idx, { start_date: e.target.value })}
                          data-testid={`contract-${idx}-start`} />
                      </Field>
                      <Field label="End date">
                        <Input type="date" value={r.end_date || ""}
                          onChange={(e) => update(idx, { end_date: e.target.value })}
                          data-testid={`contract-${idx}-end`} />
                      </Field>
                      <Field label="Value (optional)">
                        <Input type="number" value={r.value ?? ""}
                          onChange={(e) => update(idx, { value: e.target.value })}
                          data-testid={`contract-${idx}-value`} />
                      </Field>
                      <div className="md:col-span-3">
                        <Field label="Coverage notes">
                          <Input value={r.coverage_notes || ""}
                            onChange={(e) => update(idx, { coverage_notes: e.target.value })}
                            placeholder="Deploy + service · India, Singapore + UAE"
                            data-testid={`contract-${idx}-notes`} />
                        </Field>
                      </div>
                      <label className="flex items-center gap-2 text-[12px]">
                        <input type="checkbox" checked={r.active !== false}
                          onChange={(e) => update(idx, { active: e.target.checked })}
                          data-testid={`contract-${idx}-active`} />
                        Active (uncheck to terminate)
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="p-3 border-t border-slate-100">
            <Button variant="create" onClick={add} data-testid="contract-add">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add contract
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);
